import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Globe, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { trackEvent } from '../lib/posthog';
import WhatsAppIcon from './icons/WhatsAppIcon';
import Logo from './Logo';

/**
 * Header v2 (R3.5): dauerhaft hell — bg-surface/95 mit backdrop-blur und
 * Hairline von Anfang an. Die frühere Scroll-Farblogik (transparent auf dem
 * dunklen Hero) ist entfernt; der helle Hero folgt in R4. Mobile-Menü als
 * Radix Sheet: Focus-Trap, Escape schliesst, Body-Scroll-Lock, Fokus kehrt
 * zum Burger zurück.
 */
const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // "Home" entfällt — das Logo verlinkt bereits auf "/"; Kontakt bleibt als
  // normaler Nav-Punkt am Ende (der Header-CTA zeigt auf /zimmer, R1.6).
  const navigation = [
    { name: t.nav.rooms, href: '/zimmer' },
    { name: t.nav.apartments, href: '/wohnungen' },
    { name: t.nav.blog, href: '/blog' },
    { name: t.nav.forCompanies, href: '/for-companies' },
    { name: t.nav.forPropertyManagers, href: '/for-property-managers' },
    { name: t.nav.about, href: '/about' },
    { name: t.nav.contact, href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-surface/95 backdrop-blur border-b border-line"
      role="banner"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex justify-between items-center h-18 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="FeelAtHomeNow - Startseite">
            <Logo variant="light" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'text-brand-text'
                    : 'text-ink-soft hover:text-ink hover:bg-canvas'
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-3.5 right-3.5 h-0.5 bg-brand rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2">
            {/* Language Toggle (Desktop) */}
            <button
              type="button"
              onClick={toggleLanguage}
              className="hidden lg:flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-1.5 text-ink-soft hover:text-ink hover:bg-canvas transition-colors"
              aria-label={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
            >
              <Globe className="h-3.5 w-3.5" />
              <span>{language.toUpperCase()}</span>
            </button>

            {/* CTA Button */}
            <Button asChild size="sm" className="hidden lg:inline-flex">
              <Link to="/zimmer">
                {t.nav.ctaRooms}
                <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>

            {/* Language Toggle (Mobile) */}
            <button
              type="button"
              onClick={toggleLanguage}
              className="lg:hidden flex items-center gap-1 min-h-[44px] min-w-[44px] px-2.5 rounded-[10px] text-sm font-semibold text-ink-soft hover:text-ink hover:bg-canvas transition-colors"
              aria-label={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
            >
              <Globe className="h-4 w-4" />
              <span>{language.toUpperCase()}</span>
            </button>

            {/* WhatsApp (Mobile) */}
            <a
              href={buildWhatsAppUrl({ source: 'header', url: location.pathname })}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('whatsapp_click', { source: 'header' })}
              className="lg:hidden flex items-center justify-center min-h-[44px] min-w-[44px] rounded-[10px] text-ink-soft hover:text-ink hover:bg-canvas transition-colors"
              aria-label={t.whatsapp.ariaChat}
              data-testid="whatsapp-header"
            >
              <WhatsAppIcon className="h-5 w-5" />
            </a>

            {/* Mobile menu (Radix Sheet: Focus-Trap, Escape, Scroll-Lock) */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="lg:hidden flex items-center justify-center min-h-[44px] min-w-[44px] rounded-[10px] text-ink hover:bg-canvas transition-colors"
                  aria-label={menuOpen ? 'Menu schliessen' : 'Menu oeffnen'}
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-surface border-line w-80 max-w-[85vw] p-0"
                aria-describedby={undefined}
              >
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="pt-16 px-4 pb-8 space-y-1 overflow-y-auto h-full">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center justify-between px-4 py-3 min-h-[44px] text-sm font-medium rounded-[10px] transition-colors ${
                        isActive(item.href)
                          ? 'text-brand-text bg-brand-tint'
                          : 'text-ink hover:bg-canvas'
                      }`}
                    >
                      {item.name}
                      <ChevronRight className={`h-4 w-4 ${isActive(item.href) ? 'text-brand-text' : 'text-ink-faint'}`} />
                    </Link>
                  ))}

                  <Button asChild className="w-full mt-3">
                    <Link to="/zimmer" onClick={() => setMenuOpen(false)}>
                      {t.nav.ctaRooms}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
